import type { PlanarPaneContent } from '@mobile-mapping/web3d';
import type { TcRegion } from '@data-access/geospatial/scan-data';
import { Accessor, createMemo } from 'solid-js';
import { resolveTcRegionForScanData } from './station-images';

export interface PlanarStationLoadContext {
  readonly planarContents: PlanarPaneContent[];
  readonly tcRegion: TcRegion | undefined;
  readonly regions: TcRegion[] | undefined;
}

/**
 * @returns `true` when the sequential effect should **skip** (no meaningful change).
 */
export function planarStationLoadContextEquals(
  current: PlanarStationLoadContext,
  previous: PlanarStationLoadContext | undefined
): boolean {
  if (previous === undefined) {
    return false;
  }
  if (current.tcRegion?.location !== previous.tcRegion?.location) {
    return false;
  }
  if (current.planarContents.length !== previous.planarContents.length) {
    return false;
  }
  for (let i = 0; i < current.planarContents.length; i++) {
    const next = current.planarContents[i];
    const prev = previous.planarContents[i];
    if (
      next?.selectedRunId !== prev?.selectedRunId ||
      next?.selectedPointCloudTrn !== prev?.selectedPointCloudTrn ||
      next?.scanDataOnly !== prev?.scanDataOnly
    ) {
      return false;
    }
  }
  return true;
}

export function createPlanarStationLoadContextMemo(
  getPlanarContents: Accessor<PlanarPaneContent[]>,
  getRegions: Accessor<TcRegion[] | undefined>,
  getSelectedRegion: Accessor<TcRegion | undefined>,
  getSelectedLocation: Accessor<string | undefined>
): Accessor<PlanarStationLoadContext> {
  const loadContext = createMemo((): PlanarStationLoadContext => {
    const planarContents = getPlanarContents();
    const regionsList = getRegions();
    const selectedRegion = getSelectedRegion();
    const selectedLocation = getSelectedLocation();
    const tenantSample = planarContents[0]?.selectedTenantTrn;
    return {
      planarContents,
      tcRegion: resolveTcRegionForScanData(
        regionsList,
        selectedRegion,
        selectedLocation,
        tenantSample
      ),
      regions: regionsList,
    };
  });
  return loadContext;
}
